'use client';

import {useState, useEffect} from 'react';
import {useTranslations, useLocale} from 'next-intl';
import Card, {CardBody} from '@/components/ui/Card';
import ShareButton from '@/components/ui/ShareButton';

interface ScrapedArticle {
  title_fr: string;
  title_ar: string;
  body_fr: string;
  body_ar: string;
  date: string;
  category: string;
  images: string[];
  source_url: string;
}

const catStyles: Record<string, string> = {
  actualites: 'bg-primary/10 text-primary',
  aides: 'bg-accent/10 text-secondary',
  programmes: 'bg-success/10 text-success',
  accueil: 'bg-primary/10 text-primary',
  'services-actifs': 'bg-leaf/10 text-leaf',
  general: 'bg-muted/10 text-muted',
};

/* Static articles from i18n as fallback */
const staticArticles = [
  {num: 1, img: 'article_12316503_900096546726250_2776825374813697637_n.jpg', category: 'Solidarite', catClass: 'bg-primary/10 text-primary'},
  {num: 2, img: 'article_12307437_900578810011357_7519468639371756778_o.jpg', category: 'Solidarite', catClass: 'bg-primary/10 text-primary'},
  {num: 3, img: 'article_a.jpg', category: 'Distribution', catClass: 'bg-accent/10 text-accent'},
  {num: 4, img: 'article_12115650_880069948728910_5479748766687755892_n.jpg', category: 'Education', catClass: 'bg-success/10 text-success'},
  {num: 5, img: 'article_12307437_900578810011357_7519468639371756778_o.jpg', category: 'Education', catClass: 'bg-success/10 text-success'},
] as const;

export default function ActualitesPage() {
  const t = useTranslations('news');
  const locale = useLocale();
  const [scrapedArticles, setScrapedArticles] = useState<ScrapedArticle[]>([]);
  const [showScraped, setShowScraped] = useState(true);

  useEffect(() => {
    fetch('/data/scraped_articles.json')
      .then(r => r.json())
      .then((articles: ScrapedArticle[]) => {
        setScrapedArticles(articles.filter(a =>
          (a.title_fr || a.title_ar) && (a.body_fr || a.body_ar)
        ));
      })
      .catch(() => {});
  }, []);

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(locale === 'ar' ? 'ar-TN' : 'fr-TN', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="text-4xl font-bold text-primary mb-4">{t('title')}</h1>
      <p className="text-muted text-lg mb-8 max-w-3xl">{t('subtitle')}</p>

      {/* Toggle between scraped and static articles */}
      {scrapedArticles.length > 0 && (
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setShowScraped(true)}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${showScraped ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Archives UTSS ({scrapedArticles.length})
          </button>
          <button
            onClick={() => setShowScraped(false)}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${!showScraped ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Actualites recentes
          </button>
        </div>
      )}

      {showScraped && scrapedArticles.length > 0 ? (
        /* Scraped articles grid */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {scrapedArticles.map((article, index) => {
            const title = locale === 'ar' ? (article.title_ar || article.title_fr) : (article.title_fr || article.title_ar);
            const body = locale === 'ar' ? (article.body_ar || article.body_fr) : (article.body_fr || article.body_ar);
            const hasImage = article.images.length > 0;
            const catClass = catStyles[article.category] || 'bg-gray-100 text-gray-600';

            return (
              <Card key={index} hover className={index === 0 ? 'lg:col-span-2' : ''}>
                {hasImage && (
                  <div className="aspect-video bg-primary/5 rounded-t-xl overflow-hidden">
                    <img
                      src={article.images[0]}
                      alt={title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                )}
                <CardBody className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${catClass}`}>
                      {article.category.replace('-', ' ')}
                    </span>
                    <span className="text-xs text-primary font-medium">{formatDate(article.date)}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2" dir="auto">
                    {title}
                  </h3>
                  <p className="text-muted text-sm line-clamp-4 mb-3" dir="auto">
                    {body}
                  </p>
                  <ShareButton />
                </CardBody>
              </Card>
            );
          })}
        </div>
      ) : (
        /* Static articles (original i18n-based) */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {staticArticles.map(({num, img, category, catClass}, index) => (
            <Card key={num} hover className={index === 0 ? 'lg:col-span-2' : ''}>
              <div className="aspect-video bg-primary/5 rounded-t-xl overflow-hidden">
                <img
                  src={`/images/utss/${img}`}
                  alt={t(`article_${num}_title`)}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <CardBody className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${catClass}`}>{category}</span>
                  <span className="text-xs text-primary font-medium">{t(`article_${num}_date`)}</span>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2" dir="auto">
                  {t(`article_${num}_title`)}
                </h3>
                <p className="text-muted text-sm line-clamp-3 mb-3" dir="auto">
                  {t(`article_${num}_desc`)}
                </p>
                <ShareButton />
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
